import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { AskHeader, EmptyAskHeader } from "@/components/ask/header";
import { AIAnswer } from "@babackapp/ai-components/ai-answer";
import { AIPromptSuggestion } from "@babackapp/ai-components/ai-prompt-suggestion";
import { AIResultsBlock } from "@babackapp/ai-components/ai-results-block";
import { AIUserQuery } from "@babackapp/ai-components/ai-user-query";
import { AIInput, AIInputQuery } from "@babackapp/ai-components/input";
import { useAskAI } from "@babackapp/private-api-client/client/use-ask-ai";
import { useDownloadAskQuery } from "@babackapp/private-api-client/client/use-download-ask-query";
import { useAskAIFeedback } from "@babackapp/private-api-client/client/use-ask-ai-feedback";
import { AskResponseDto } from "@babackapp/private-api-client/types";
import { BanknoteIcon, CalendarIcon, Loader2, User2Icon } from "lucide-react";
import { useState } from "react";

export default function AskPage() {
  const [query, setQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const { data: shop } = useCurrentShopSuspense();
  const { mutate: ask, isPending: isLoading, data } = useAskAI({ shop });

  const onSubmit = () => {
    setQuery(inputValue);
    setInputValue("");
    ask(inputValue);
  };

  const onSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setInputValue("");
    ask(suggestion);
  };

  const isEmpty = !data && !isLoading;

  return (
    <div className="flex justify-center items-center w-full">
      <div className="min-h-full flex flex-col gap-4 items-center justify-center pt-16 w-[700px]">
        {isEmpty ? (
          <EmptyChat onSuggestionClick={onSuggestionClick} />
        ) : (
          <NonEmptyChat
            shopId={shop?.id}
            query={{ content: query }}
            data={data}
            isLoading={isLoading}
          />
        )}
        <AIInput
          isLoading={isLoading}
          onSubmit={onSubmit}
          value={inputValue}
          onChange={setInputValue}
        />
      </div>
    </div>
  );
}

type NonEmptyChatProps = {
  query: AIInputQuery;
  data?: AskResponseDto | null;
  isLoading: boolean;
  shopId: string;
};

function NonEmptyChat({ query, data, isLoading, shopId }: NonEmptyChatProps) {
  const error = data?.results[0].error;
  const download = useDownloadAskQuery(data?.results[0].result ?? []);
  const [feedback, setFeedback] = useState<"positive" | "negative" | null>(
    null,
  );
  const { mutate: sendFeedback } = useAskAIFeedback();

  const handleFeedback = (isPositive: boolean) => {
    setFeedback(isPositive ? "positive" : "negative");
    if (data?.queryId) {
      sendFeedback({
        shopId: shopId,
        queryId: data.queryId,
        userIsOkWithAnswer: isPositive,
      });
    }
  };

  return (
    <div className="flex flex-col gap-4 items-center flex-1 w-full">
      <AskHeader />
      <AIResultsBlock>
        <AIUserQuery content={query.content} />
        {data ? (
          <AIAnswer
            onDownloadResults={download}
            content={error}
            data={data.results[0].result}
            onFeedback={handleFeedback}
            selectedFeedback={feedback}
          />
        ) : isLoading ? (
          <Loader2 className="animate-spin size-4" />
        ) : null}
      </AIResultsBlock>
    </div>
  );
}

type EmptyChatProps = {
  onSuggestionClick: (suggestion: string) => void;
};

function EmptyChat({ onSuggestionClick }: EmptyChatProps) {
  const suggestions = [
    {
      icon: <User2Icon size={16} className="text-yellow-500" />,
      text: "3 dernières demande de retour",
    },
    {
      icon: <BanknoteIcon size={16} className="text-green-600" />,
      text: "Montant total des resolution échanges",
    },
    {
      icon: <CalendarIcon size={16} className="text-orange-600" />,
      text: "Nombre total d'échanges par jour de la semaine",
    },
  ];

  return (
    <div className="flex flex-col h-full w-full justify-center gap-4 items-center flex-1">
      <EmptyAskHeader />
      <p className="max-w-[400px] text-center text-muted-foreground text-sm font-medium">
        Demandez-moi tout ce que vous voulez savoir sur les demandes de retour
        de votre dashboard Baback, ou inspirez-vous d'un template existant.
      </p>
      <div className="flex gap-2 flex-wrap mt-4">
        {suggestions.map((suggestion, index) => (
          <AIPromptSuggestion
            key={index}
            icon={suggestion.icon}
            onClick={() => onSuggestionClick(suggestion.text)}
          >
            {suggestion.text}
          </AIPromptSuggestion>
        ))}
      </div>
    </div>
  );
}
